import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { INITIAL_PAYMENT_OPTIONS, MixError, Payment, PaymentMethod, PaymentOption } from "../constants";


const getPaymentEligibility = (priceInPoints: number, userTotalPoints: number) => ({
  points: userTotalPoints >= priceInPoints,
  cash: true,
  mix: true
});


type usePaymentReturnType = {
  paymentOptions: PaymentOption[];
  selectedPayment: Payment | undefined;
  setSelectedPayment: Dispatch<SetStateAction<Payment | undefined>>;
  selectPaymentMethod: (paymentMethod: PaymentMethod) => void;
  updateMix: (points: number) => void;
  maxMixPoints: number;
  mixError: MixError | null;
}

export default function usePayment({
  priceInPoints,
  priceInCash,
  conversionFactor,
  userTotalPoints,
  isFetching,
}: {
  conversionFactor: number;
  priceInPoints: number;
  priceInCash: number;
  userTotalPoints: number;
  isFetching?: boolean;
}): usePaymentReturnType {
  const [selectedPayment, setSelectedPayment] = useState<Payment>();
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>(INITIAL_PAYMENT_OPTIONS);
  const hasInitialized = useRef(false);

  const maxMixPoints = Math.min(userTotalPoints, priceInPoints);

  const buildPayment = (paymentMethod: PaymentMethod): Payment => {
    if (paymentMethod === "points") return { paymentMethod, cash: 0, points: priceInPoints };
    if (paymentMethod === "cash") return { paymentMethod, cash: priceInCash, points: 0 };
    return {
      paymentMethod,
      points: maxMixPoints,
      cash: (priceInPoints - maxMixPoints) / conversionFactor,
    };
  };

  const selectPaymentMethod = (paymentMethod: PaymentMethod) => setSelectedPayment(buildPayment(paymentMethod));

  const buildMixPayment = (points: number): Payment => ({
    paymentMethod: "mix",
    points,
    cash: Math.max(priceInPoints - points, 0) / conversionFactor,
  });

  const updateMix = (points: number) => setSelectedPayment(buildMixPayment(Math.max(points, 0)));

  const getMixError = (): MixError | null => {
    if (selectedPayment?.paymentMethod !== "mix" || selectedPayment.points <= maxMixPoints) return null;
    if (userTotalPoints < priceInPoints) return "insufficientPoints";
    return "exceedsPrice";
  };

  const mixError = getMixError();

  useEffect(() => {
    if (isFetching) return;

    const eligibility = getPaymentEligibility(priceInPoints, userTotalPoints);
    setPaymentOptions((options) =>
      options.map((option) => ({ ...option, allowed: eligibility[option.paymentMethod] }))
    );

    if (!hasInitialized.current) {
      hasInitialized.current = true;
      const initialMethod: PaymentMethod = eligibility.points ? "points" : eligibility.mix ? "mix" : "cash";
      setSelectedPayment(buildPayment(initialMethod));
      return;
    }

    setSelectedPayment((prev) => {
      if (!prev) return prev;
      if (prev.paymentMethod !== "mix") return buildPayment(prev.paymentMethod);
      return buildMixPayment(prev.points);
    });
  }, [isFetching, priceInPoints, userTotalPoints]);

  return { paymentOptions, selectedPayment, setSelectedPayment, selectPaymentMethod, updateMix, maxMixPoints, mixError };
}

export type { usePaymentReturnType };
